import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list with performance optimizations
 */
export default class ResultsList extends PaginatedList {
  #intersectionObserver = null;
  #loadingThreshold = 2000; // Start loading when 2000px from bottom
  #loadingDebounceTimer = null;
  #preloadedPages = new Set();

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#setupIntersectionObserver();
    this.#preloadNextPage();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#intersectionObserver?.disconnect();
    clearTimeout(this.#loadingDebounceTimer);
  }

  /**
   * Set up intersection observer for more efficient scroll detection
   */
  #setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: `0px 0px ${this.#loadingThreshold}px 0px`,
      threshold: 0.1
    };

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.#debouncedLoad();
        }
      });
    }, options);

    // Observe the last item or a sentinel element
    this.#observeLastItem();
  }

  /**
   * Observe the last item in the list for intersection
   */
  #observeLastItem() {
    const lastItem = this.querySelector('.product-grid__item:last-child');
    if (lastItem) {
      this.#intersectionObserver?.observe(lastItem);
    }
  }

  /**
   * Debounced loading to prevent rapid fire requests
   */
  #debouncedLoad = () => {
    clearTimeout(this.#loadingDebounceTimer);
    this.#loadingDebounceTimer = setTimeout(() => {
      this.#loadNextPageIfNeeded();
    }, 100);
  };

  /**
   * Load next page if conditions are met
   */
  async #loadNextPageIfNeeded() {
    const currentPage = this.getAttribute('current-page') || 1;
    const hasMorePages = this.getAttribute('has-more-pages') === 'true';
    
    if (!hasMorePages || this.#preloadedPages.has(currentPage + 1)) {
      return;
    }

    try {
      await this.#loadPage(currentPage + 1);
      this.#preloadedPages.add(currentPage + 1);
      
      // Update observer to watch new last item
      requestIdleCallback(() => {
        this.#observeLastItem();
      });
    } catch (error) {
      console.error('Failed to load next page:', error);
    }
  }

  /**
   * Preload the next page in the background
   */
  #preloadNextPage() {
    requestIdleCallback(() => {
      const currentPage = parseInt(this.getAttribute('current-page') || '1');
      const nextPage = currentPage + 1;
      
      if (!this.#preloadedPages.has(nextPage)) {
        this.#preloadPage(nextPage);
      }
    });
  }

  /**
   * Preload a page without rendering
   */
  async #preloadPage(pageNumber) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', pageNumber);
      
      const response = await fetch(url.href, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (response.ok) {
        const html = await response.text();
        // Store preloaded content
        sessionStorage.setItem(`page-${pageNumber}`, html);
        this.#preloadedPages.add(pageNumber);
      }
    } catch (error) {
      console.warn(`Failed to preload page ${pageNumber}:`, error);
    }
  }

  /**
   * Load a specific page with caching
   */
  async #loadPage(pageNumber) {
    // Check if page is already cached
    const cachedContent = sessionStorage.getItem(`page-${pageNumber}`);
    if (cachedContent) {
      this.#renderCachedContent(cachedContent);
      return;
    }

    // Show loading indicator
    this.#showLoadingIndicator();

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', pageNumber);
      
      const response = await fetch(url.href, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      this.#renderNewContent(html);
      
      // Cache for future use
      sessionStorage.setItem(`page-${pageNumber}`, html);
      
    } catch (error) {
      this.#showErrorMessage();
      throw error;
    } finally {
      this.#hideLoadingIndicator();
    }
  }

  /**
   * Render cached content
   */
  #renderCachedContent(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newItems = doc.querySelectorAll('.product-grid__item');
    
    // Use document fragment for efficient DOM manipulation
    const fragment = document.createDocumentFragment();
    newItems.forEach(item => fragment.appendChild(item.cloneNode(true)));
    
    const grid = this.refs?.grid || this.querySelector('.product-grid');
    if (grid) {
      grid.appendChild(fragment);
    }
  }

  /**
   * Render new content with optimization
   */
  #renderNewContent(html) {
    // Use requestAnimationFrame for smooth rendering
    requestAnimationFrame(() => {
      this.#renderCachedContent(html);
      
      // Lazy load images in new content
      this.#lazyLoadImages();
    });
  }

  /**
   * Lazy load images for better performance
   */
  #lazyLoadImages() {
    const images = this.querySelectorAll('img[data-src]');
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          imageObserver.unobserve(img);
        }
      });
    });

    images.forEach(img => imageObserver.observe(img));
  }

  /**
   * Show loading indicator
   */
  #showLoadingIndicator() {
    let loader = this.querySelector('.loading-indicator');
    if (!loader) {
      loader = document.createElement('div');
      loader.className = 'loading-indicator';
      loader.innerHTML = `
        <div class="loading-spinner">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="10" stroke-width="2" opacity="0.3"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke-width="2">
              <animateTransform attributeName="transform" type="rotate" dur="1s" repeatCount="indefinite" values="0 12 12;360 12 12"/>
            </path>
          </svg>
          Loading more products...
        </div>
      `;
      this.appendChild(loader);
    }
    loader.style.display = 'block';
  }

  /**
   * Hide loading indicator
   */
  #hideLoadingIndicator() {
    const loader = this.querySelector('.loading-indicator');
    if (loader) {
      loader.style.display = 'none';
    }
  }

  /**
   * Show error message
   */
  #showErrorMessage() {
    const errorMsg = document.createElement('div');
    errorMsg.className = 'error-message';
    errorMsg.innerHTML = `
      <p>Failed to load more products. <button onclick="location.reload()">Try again</button></p>
    `;
    this.appendChild(errorMsg);
  }

  /**
   * Updates the layout with performance optimization.
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout with view transition.
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Set layout efficiently.
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    
    // Batch DOM updates
    requestAnimationFrame(() => {
      grid.setAttribute('product-grid-view', value);
    });
  }

  /**
   * Handles media query changes efficiently.
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    requestAnimationFrame(() => {
      targetElement.checked = true;
      this.#setLayout('default');
    });
  };

  /**
   * Clean up cached pages to prevent memory issues
   */
  #cleanupCache() {
    const keys = Object.keys(sessionStorage);
    const pageKeys = keys.filter(key => key.startsWith('page-'));
    
    // Keep only last 10 pages in cache
    if (pageKeys.length > 10) {
      const sortedKeys = pageKeys.sort((a, b) => {
        const pageA = parseInt(a.split('-')[1]);
        const pageB = parseInt(b.split('-')[1]);
        return pageA - pageB;
      });
      
      sortedKeys.slice(0, -10).forEach(key => {
        sessionStorage.removeItem(key);
      });
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}